import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'

import { DEFAULT_INVOICE_LANGUAGE } from '@features/invoices/constants/defaultInvoiceLanguage'
import { defaultInvoiceTextTranslationKeys } from '@features/invoices/constants/defaultInvoiceTextTranslationKeys'
import { invoiceLanguageToLocale } from '@features/invoices/constants/invoiceLanguageToLocale'
import { getDefaultInvoiceLocale } from '@features/invoices/utils/getDefaultInvoiceLocale'

import { useZervantSettings } from '../../../hooks/useZervantSettings'
import type { ModelDocumentType } from '../../../types/zervantSettingsModelDocumentType'
import { getSettingsFieldsByDocumentType } from '../../../utils/getSettingsFieldsByDocumentType'
import { getDocumentTypeSettingsColumnNames } from '../utils/getDocumentTypeSettingsColumnNames'
import type { DocumentTypeSettingsDefaultTexts } from '../utils/getDocumentTypeSettingsFormValues'
import { getDocumentTypeSettingsFormValues } from '../utils/getDocumentTypeSettingsFormValues'

export const usePredefinedDocumentTypeSettingsFormValues = (modelType: ModelDocumentType) => {
  const { t } = useTranslation()
  const { data, hasFailedToLoad, isLoading, refetch } = useZervantSettings()

  const { defaultTexts, predefinedDocumentTypeSettingsFormValues, savedColumnNames } = useMemo(() => {
    const fields = getSettingsFieldsByDocumentType(data, modelType)
    const locale =
      getDefaultInvoiceLocale(fields.language?.default) ?? invoiceLanguageToLocale[DEFAULT_INVOICE_LANGUAGE]
    const texts: DocumentTypeSettingsDefaultTexts = {
      footerNote: t(defaultInvoiceTextTranslationKeys.footerNote, { lng: locale }),
      message: t(defaultInvoiceTextTranslationKeys.message, { lng: locale }),
    }

    return {
      defaultTexts: texts,
      predefinedDocumentTypeSettingsFormValues: getDocumentTypeSettingsFormValues(fields, texts),
      savedColumnNames: getDocumentTypeSettingsColumnNames(fields),
    }
  }, [data, modelType, t])

  return {
    defaultTexts,
    hasFailedToLoad,
    isLoading,
    predefinedDocumentTypeSettingsFormValues,
    refetch,
    savedColumnNames,
  }
}
